import { ControlIcon, EmptyState, LazyLoader, Modal } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { FC, Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TimetableVersionResourceType,
  useTimetableVersionConstraintsQuery,
} from '../../../types/planung-graphql-client-defs';
import { ConstraintsFormInitialValuesType } from '../../Constraints/types';
import { useTimetableStore } from '../TimetableProvider';
import styles from './ConstraintModal.module.scss';
import { ConstraintModalItem } from './ContraintModalItem';

export const ConstraintModal: FC = observer(() => {
  const { t } = useTranslation();

  const pinboardStore = useTimetableStore();

  const selectedCard = pinboardStore.getCardToShowOnInfo();

  const uuids = useMemo(() => {
    return selectedCard?.lessonClasses.map((lessonClass) => lessonClass.class.uuid);
  }, [selectedCard?.lessonClasses]);

  const versionUuid = pinboardStore.getCurrentVersion()?.uuid ?? '';

  const [{ data }] = useTimetableVersionConstraintsQuery({
    variables: { versionUuid, resourceType: TimetableVersionResourceType.Class, resourceUuids: uuids ?? [] },
    pause: !versionUuid || !uuids,
  });

  const constraintsData = data?.timetableVersionConstraints ?? [];

  const hasConstraints = constraintsData.filter((c) => !c.constraints).length === 0;

  return (
    <Modal
      className={styles['constraint-modal']}
      isOpen={pinboardStore.constraintModal.isOpen}
      onRequestClose={() => pinboardStore.setConstraintModalOpen(false)}
      title={t('pinboard.checkConstraints', { title: selectedCard?.subject?.name })}
      parent={document.fullscreenElement ? document.getElementById('timetable-grid') : null}
    >
      <Suspense fallback={<LazyLoader forceHeight='15vh' embedded />}>
        <div className={styles.constraints}>
          {!hasConstraints ? (
            <EmptyState icon={<ControlIcon />} title={t('pinboard.noConstraints')} />
          ) : (
            constraintsData.map((constraint, i) => {
              const classUuid =
                selectedCard?.lessonClasses.find((lessonClass) => {
                  return lessonClass.class.uuid === constraint.resourceUuid;
                })?.class.uuid ?? '';

              const title = selectedCard?.classes.find((c) => c.uuid === classUuid)?.name ?? '';

              const constraintsData = JSON.parse(
                constraint.constraints ??
                  JSON.stringify({
                    comment: '',
                  }),
              ) as ConstraintsFormInitialValuesType;

              return <ConstraintModalItem key={i} title={title} constraintsData={constraintsData} />;
            })
          )}
        </div>
      </Suspense>
    </Modal>
  );
});
